import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Paper,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { getDashboardData, DashboardData } from '../services/api';
import { brand } from '../theme';

interface MetricCard {
  title: string;
  value: number;
  color: string;
  percentage?: number;
}

const Dashboard: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6); // Go back 6 months
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getDashboardData('home', startDate, endDate);
      setDashboardData(response);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setError(`Failed to fetch dashboard data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDashboardData();
  }, [startDate, endDate, fetchDashboardData]);

  // Extract data from dashboard response
  const kpis = dashboardData?.kpis || {
    survival_rate: 0,
    mortality_rate: 0,
    export_rate: 0,
    species_diversity: 0,
  };

  // Get the latest cumulative data point for totals
  const latestData = dashboardData?.cumulatives?.byDay?.slice(-1)[0] || {
    total: 0,
    alive: 0,
    deceased: 0,
    exported: 0,
  };

  // Calculate metrics from backend data
  const totalCorals = latestData.total;
  const aliveCorals = latestData.alive;
  const exportedCorals = latestData.exported;
  const deceasedCorals = latestData.deceased;
  const totalSpecies = kpis.species_diversity;

  // Calculate percentages (backend already provides rates, but convert to percentages)
  const alivePercentage = Math.round(kpis.survival_rate * 100);
  const mortalityRate = Math.round(kpis.mortality_rate * 100);
  const exportedPercentage = Math.round(kpis.export_rate * 100);

  // Prepare metric cards
  const metricCards: MetricCard[] = [
    { title: 'Total Corals', value: totalCorals, color: brand.navy },
    { title: 'Alive', value: aliveCorals, color: '#2e7d32', percentage: alivePercentage },
    { title: 'Exported', value: exportedCorals, color: '#ed6c02', percentage: exportedPercentage },
    { title: 'Deceased', value: deceasedCorals, color: '#d32f2f', percentage: mortalityRate },
    { title: 'Species Count', value: totalSpecies, color: brand.navyMid },
  ];

  // Get species distribution from backend data
  const speciesData =
    dashboardData?.species_count?.top?.map((species) => ({
      species: species.speciesCode.replace(/-/g, ' '),
      count: species.count,
      percentage: Math.round(species.pct * 100),
    })) || [];

  // Get time series data from backend (already cumulative)
  const timeSeriesData = dashboardData?.cumulatives?.byDay || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ALIVE':
        return 'success';
      case 'EXPORTED':
        return 'warning';
      case 'DECEASED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Coral Management Dashboard
      </Typography>

      {/* Date Range Selector */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', paddingLeft: 11 }}>
          Date Range Filter
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Metric Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' },
              gap: 3,
              mb: 3,
            }}
          >
            {metricCards.map((metric, index) => (
              <Card key={index} sx={{ height: '100%' }}>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {metric.title}
                  </Typography>
                  <Typography variant="h4" sx={{ color: metric.color, fontWeight: 'bold' }}>
                    {metric.value.toLocaleString()}
                  </Typography>
                  {metric.percentage !== undefined && (
                    <Typography variant="body2" color="textSecondary">
                      {metric.percentage}% of total
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Data Tables and Charts */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
              gap: 3,
              mb: 3,
            }}
          >
            {/* Coral Activity Over Time Chart */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cumulative Coral Counts Over Time
                  </Typography>
                  <Box sx={{ width: '100%', height: 400 }}>
                    <ResponsiveContainer>
                      <LineChart
                        data={timeSeriesData}
                        margin={{
                          top: 5,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="date"
                          tick={{ fontSize: 10 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval="preserveStartEnd"
                          tickFormatter={(value) => {
                            // Format date nicely (e.g., "Nov 5" or "2025-11-05")
                            const date = new Date(value);
                            return date.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            });
                          }}
                        />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          labelFormatter={(value) => {
                            const date = new Date(value as string | number);
                            return `Date: ${date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}`;
                          }}
                          formatter={(value, name) => [value, `${name} corals (cumulative)`]}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="alive"
                          stroke="#2e7d32"
                          strokeWidth={1}
                          dot={{ r: 2 }}
                          name="Alive"
                        />
                        <Line
                          type="monotone"
                          dataKey="exported"
                          stroke="#ed6c02"
                          strokeWidth={1}
                          dot={{ r: 2 }}
                          name="Exported"
                        />
                        <Line
                          type="monotone"
                          dataKey="deceased"
                          stroke="#d32f2f"
                          strokeWidth={1}
                          dot={{ r: 2 }}
                          name="Deceased"
                        />
                        <Line
                          type="monotone"
                          dataKey="total"
                          stroke={brand.navy}
                          strokeWidth={2}
                          dot={false}
                          name="Total"
                          strokeDasharray="5 5"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Key Metrics Summary */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Key Performance Indicators
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Survival Rate
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#2e7d32' }}>
                        {alivePercentage}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Mortality Rate
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#d32f2f' }}>
                        {mortalityRate}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Export Success Rate
                      </Typography>
                      <Typography variant="h5" sx={{ color: '#ed6c02' }}>
                        {exportedPercentage}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        Species Diversity
                      </Typography>
                      <Typography variant="h5" sx={{ color: brand.navyMid }}>
                        {totalSpecies} species
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>

          {/* Second Row: Species and Recent Corals */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 3,
              mb: 3,
            }}
          >
            {/* Top Species */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Top Species (by Count)
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Species</TableCell>
                          <TableCell align="right">Count</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {speciesData.slice(0, 8).map((species, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {species.species.toLowerCase()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={species.count} size="small" />
                            </TableCell>
                            <TableCell align="right">{species.percentage}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            {/* Recent Corals */}
            <Box>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Recent Coral Entries
                  </Typography>
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Species</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Date Added</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(dashboardData?.recent_entries || []).slice(0, 8).map((coral, index) => {
                          const species = coral.speciesCode || 'UNKNOWN';
                          const status = coral.status || 'UNKNOWN';
                          const createdAt = coral.createdAt || '';

                          return (
                            <TableRow key={coral.coralId || index} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                  {species.toLowerCase().replace(/-/g, ' ')}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={status}
                                  size="small"
                                  color={getStatusColor(status) as any}
                                />
                              </TableCell>
                              <TableCell>
                                {createdAt ? new Date(createdAt).toLocaleDateString() : 'N/A'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
